import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
} from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import ChatIcon from "@mui/icons-material/Chat";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import messagingScreenshot1 from "../../../images/features/messaging-1.png";
import messagingScreenshot2 from "../../../images/features/messaging-2.png";

const InAppMessaging = () => {
  const theme = useTheme();

  const benefits = [
    {
      icon: <ChatIcon />,
      title: "Real-Time Chat",
      description: "Instant messaging with your cleaning team. No more texts or phone calls.",
    },
    {
      icon: <NotificationsIcon />,
      title: "Push Notifications",
      description: "Get notified instantly when cleaners message or update job status.",
    },
    {
      icon: <AttachFileIcon />,
      title: "File Sharing",
      description: "Share access codes, instructions, and documents securely.",
    },
    {
      icon: <ImageIcon />,
      title: "Photo Sharing",
      description: "Send and receive photos directly in chats.",
    },
    {
      icon: <GroupIcon />,
      title: "Group Chats",
      description: "Create property-specific groups for multiple cleaners.",
    },
    {
      icon: <CheckCircleIcon />,
      title: "Read Receipts",
      description: "Know when your messages have been seen.",
    },
  ];

  const stats = [
    { value: "99%", label: "Response rate" },
    { value: "<2min", label: "Avg response" },
    { value: "100%", label: "In-app" },
    { value: "24/7", label: "Available" },
  ];

  return (
    <FeatureLayout
      title="In-App Messaging"
      subtitle="All communication in one place"
      description="Stop juggling between texts, calls, and emails. Everything is organized in one thread. Share access codes, special instructions, and photos securely. Never lose important conversations again."
      icon={<MessageIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #4facfe, #00f2fe)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "In-App Messaging" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={messagingScreenshot1}
      imageLeft={messagingScreenshot2}
    >
      {/* Template Messages */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Smart Features for Better Communication
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: "100%",
              }}
            >
              <Typography variant="h6" fontWeight="800" gutterBottom>
                📝 Saved Replies
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Save time with pre-written responses for common questions.
              </Typography>
              <List dense>
                {[
                  "Access code: [property]",
                  "Parking instructions",
                  "Special cleaning requests",
                  "Emergency contacts",
                ].map((text, idx) => (
                  <ListItem key={idx} dense>
                    <ListItemIcon><CheckCircleIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: "100%",
              }}
            >
              <Typography variant="h6" fontWeight="800" gutterBottom>
                🔔 Smart Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Get notified only about what matters to you.
              </Typography>
              <List dense>
                {[
                  "New messages",
                  "Job status changes",
                  "Photo uploads",
                  "Emergency alerts",
                ].map((text, idx) => (
                  <ListItem key={idx} dense>
                    <ListItemIcon><CheckCircleIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Translation */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.dark, 0.02)} 100%)`,
            textAlign: "center",
          }}
        >
          <Typography variant="h5" fontWeight="800" gutterBottom>
            🌐 Built-in Translation
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Communicate effortlessly with cleaners who speak different languages.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports 100+ languages
          </Typography>
        </Paper>
      </Container>
    </FeatureLayout>
  );
};

export default InAppMessaging;